import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  Text,
  AlertDialogBody,
  AlertDialogFooter,
  ButtonGroup,
  Button,
  Input,
  FormControl,
  useToast,
  SkeletonText,
  FormLabel,
} from '@chakra-ui/react';
import { history } from 'features/common/history/history';
import { useRef, useState } from 'react';
import { contextIdsRoute } from '../../../../routes';
import { T } from '@transifex/react';
import { useDeleteSitecoreContext } from '../../../../api/useDeleteSitecoreContext';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router';
import { t } from '@transifex/native';
import { useGetSitecoreContextId } from 'features/context-ids/api/useGetSitecoreContextId';

const DeleteContextIdModal = () => {
  const [inputValue, setInputValue] = useState('');
  const lessDestructiveRef = useRef(null);

  const { sitecoreContextId } = useParams<{ sitecoreContextId: string }>();

  const { data, isLoading: isContextIdLoading } = useGetSitecoreContextId({
    sitecoreContextId,
  });
  const {
    mutate: deleteSitecoreContextMutation,
    isLoading: isLoadingDeleteRequest,
  } = useDeleteSitecoreContext();
  const toast = useToast();
  const queryClient = useQueryClient();

  const onClose = () => history.goBack();

  const onDelete = () => {
    deleteSitecoreContextMutation(
      { id: sitecoreContextId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('contexts/list');
          history.replace(`${contextIdsRoute}${history.location.search}`);
          toast({
            description: t(`Context ID ${sitecoreContextId} deleted.`),
            status: 'success',
          });
        },
        onError: () => {
          toast({
            description: t(`Context ID ${sitecoreContextId} deletion failed.`),
            status: 'error',
          });
        },
      },
    );
  };

  const handleDelete = () => {
    if (inputValue === 'delete') {
      onDelete();
    }
  };
  return (
    <AlertDialog
      isOpen={true}
      onClose={onClose}
      leastDestructiveRef={lessDestructiveRef}
      size='xl'
      autoFocus={false}
    >
      <AlertDialogOverlay>
        <AlertDialogContent data-testid='delete-context-ids-modal' gap={2}>
          <AlertDialogHeader>
            <T _str='Delete Context ID' />
          </AlertDialogHeader>
          <AlertDialogBody>
            {isContextIdLoading ? (
              <SkeletonText noOfLines={1} width='90%' />
            ) : (
              <Text>
                Are you sure you want to delete {sitecoreContextId}
                {data?.name ? `, ${data?.name}` : ''}?
              </Text>
            )}
            <Text>
              All apps and services using this Context ID will stop working and
              must be updated with a new Context ID.
            </Text>
            <FormControl py={8}>
              <FormLabel
                htmlFor='delete-confirm-input'
                data-testid='delete-confirm-label'
                srOnly
              >
                Type "delete" to confirm
              </FormLabel>
              <Text data-testid='delete-confirm-description'>
                Type "delete" to confirm
              </Text>
              <Input
                id='delete-confirm-input'
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                mt={2}
              />
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <ButtonGroup>
              <Button
                variant='ghost'
                data-testid='delete-context-ids-modal-cancel-btn'
                onClick={onClose}
                isDisabled={isLoadingDeleteRequest}
              >
                Cancel
              </Button>
              <Button
                colorScheme='danger'
                variant='solid'
                data-testid='delete-context-ids-modal-delete-btn'
                onClick={handleDelete}
                isDisabled={
                  inputValue !== 'delete' ||
                  isLoadingDeleteRequest ||
                  isContextIdLoading
                }
                isLoading={isLoadingDeleteRequest || isContextIdLoading}
              >
                Delete
              </Button>
            </ButtonGroup>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
export default DeleteContextIdModal;
